import * as Sentry from "@sentry/react";
import { getClientIdentifiers } from "@/lib/client-identifiers";
import { decodeJWT, getAccessToken } from "@/lib/token-utils";

const DEFAULT_DSN =
  "https://8e9c1eab819b5c6f03ae83d00f3cfec5@o4510526930747392.ingest.us.sentry.io/4510538178428928";

let initialized = false;

function applyAuthContext(deviceId: string) {
  const token = getAccessToken();
  if (!token) {
    Sentry.setUser({ id: deviceId });
    Sentry.setContext("auth", { loggedIn: false });
    return;
  }

  const payload = decodeJWT(token);
  if (!payload) {
    Sentry.setUser({ id: deviceId });
    Sentry.setContext("auth", { loggedIn: true, tokenDecoded: false });
    return;
  }

  // Prefer app-level user id when available; fall back to device id.
  const userId = payload.user_id || deviceId;
  Sentry.setUser({
    id: userId,
    email: payload.email,
  });

  if (payload.tenant_id) Sentry.setTag("tenant_id", payload.tenant_id);
  if (payload.role) Sentry.setTag("role", payload.role);
  if (payload.user_id) Sentry.setTag("user_id", payload.user_id);

  Sentry.setContext("auth", {
    loggedIn: true,
    user_id: payload.user_id,
    tenant_id: payload.tenant_id,
    role: payload.role,
  });
}

export function initSentry() {
  if (initialized) return;
  initialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN || DEFAULT_DSN;
  const { deviceId, sessionId } = getClientIdentifiers();

  Sentry.init({
    dsn,
    enabled: Boolean(dsn) && import.meta.env.PROD,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sendDefaultPii: true,
    attachStacktrace: true,
    maxBreadcrumbs: 200,
    integrations: [
      Sentry.replayIntegration({
        // Good defaults to avoid collecting sensitive data in replays.
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error", "log", "info"] }),
    ],
    // Disable tracing for now
    tracesSampleRate: 0,
    // Session Replay (only on errors)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Enable Sentry Logs
    _experiments: {
      enableLogs: true,
    },
    beforeSend(event) {
      event.tags = {
        ...event.tags,
        session_id: sessionId,
        device_id: deviceId,
      };
      return event;
    },
  });

  // Stable identifiers you can search/filter by.
  Sentry.setTag("session_id", sessionId);
  Sentry.setTag("device_id", deviceId);
  Sentry.setContext("client", {
    deviceId,
    sessionId,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  });

  applyAuthContext(deviceId);

  // Keep user context in sync across login/logout.
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
      if (e.key === "authToken") applyAuthContext(deviceId);
    });
  }

  Sentry.addBreadcrumb({
    category: "app.lifecycle",
    message: "dashboard_boot",
    level: "info",
  });
}

// Side-effect init (simple + ensures early initialization)
initSentry();


